/**
 * DashboardPanel — spend summary by cost centre.
 *
 * Three cost centres now: Outreach 1 / Outreach 2 / HQ. No team split.
 * Each cost centre shows: income · expense · net + a category tile grid.
 * Amounts are displayed as magnitudes (money = Math.abs).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  TrendingUp, TrendingDown, AlertTriangle, Hotel, Fuel, Gift,
  Users, Building2, Truck, Landmark, HeartPulse, CreditCard, PartyPopper, DollarSign,
  Stethoscope, Laptop, Package, Briefcase, ParkingCircle, Key, Share2, Crown, Shield, Wallet, User, Undo2, HelpCircle, Repeat,
} from "lucide-react";
import { getDashboard, getLoansSummary, money } from "@/lib/operations/api";
import CategoryMonthlyChart from "./CategoryMonthlyChart";
import CategoryDetailCard from "./CategoryDetailCard";
import BudgetsDialog from "./BudgetsDialog";
import TransactionsDrillDialog from "./TransactionsDrillDialog";

const CAT_ICON = {
  hotels: Hotel, gas: Fuel, incentives: Gift, staff: Users,
  office: Building2, travel: Truck, adherence: HeartPulse,
  banking: CreditCard, entertainment: PartyPopper, clinical: Stethoscope,
  technology: Laptop, supplies: Package, consultant: Briefcase,
  parking: ParkingCircle, rent: Key, referrals: Share2, management: Crown,
  insurance: Shield, income: TrendingUp, personal: User, refunds: Undo2, subscriptions: Repeat, unknown: HelpCircle, other: DollarSign,
};

export default function DashboardPanel({ config, refreshKey }) {
  // Default to "" (Total = All cost centres) so the Dashboard opens on
  // the aggregate view, matching how the Review tab starts on the "All"
  // category filter.
  const [cc, setCc] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all");
  // Source filter — "" (all) | "import" (statement) | "manual". Sent to
  // /api/operations/dashboard so all totals + charts scope the same way.
  const [sourceFilter, setSourceFilter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [budgetsOpen, setBudgetsOpen] = useState(false);
  const [drill, setDrill] = useState(null); // { category, month } | null
  const [loans, setLoans] = useState(null); // { count, total, paid_out, received }

  // "" (empty string) represents the aggregate "All cost centres" view.
  // Any real cost-centre key filters the dashboard to that centre.
  const isAll = cc === "";
  const ccLabel = useMemo(
    () => (isAll ? "All cost centres" :
           config.cost_centres.find((x) => x.key === cc)?.label || cc),
    [config, cc, isAll],
  );

  const reload = useCallback(async () => {
    setLoading(true);
    try { setData(await getDashboard(cc || undefined, sourceFilter || undefined)); }
    catch { setData(null); }
    finally { setLoading(false); }
    // Loans are org-wide (not scoped to a cost centre) — fetch alongside.
    try { setLoans(await getLoansSummary()); } catch { setLoans(null); }
  }, [cc, sourceFilter]);

  useEffect(() => { reload(); }, [reload, refreshKey]);

  // When switching cost centre, keep the selected month if it exists in the
  // new dataset, otherwise fall back to "all".
  useEffect(() => {
    if (selectedMonth === "all") return;
    if (!data?.months?.includes(selectedMonth)) setSelectedMonth("all");
  }, [data, selectedMonth]);

  return (
    <div className="space-y-6">
      {/* Toolbar — mirrors the Review tab exactly:
            [Category chip row (applicable only)]
            [Cost centre pill] [Source pill] [Set budgets outline]
          Same heights, same rounded-lg category chips, same rounded-full
          pill toggles, same right-side outline action button. */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Category chip row — always renders (regardless of load
              state) so the toolbar layout stays stable when the tab
              is first opened. Chips get the teal-fill treatment once
              `data.by_category` is populated. */}
          <div className="w-full" data-testid="ops-dashboard-cat-jump">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                Category
              </p>
              <div className="flex flex-wrap gap-1.5">
                {/* "All" chip mirrors Review's default chip — always
                    renders in the active navy style and closes any
                    open drill dialog so tapping it feels like "reset". */}
                <button
                  key="__all__"
                  type="button"
                  onClick={() => setDrill(null)}
                  className="inline-flex items-center justify-center h-8 px-3 rounded-lg border text-xs font-semibold transition min-w-0 bg-brand-navy text-white border-brand-navy shadow-sm"
                  data-testid="ops-dashboard-cat-jump-all"
                >
                  <span className="truncate">All</span>
                </button>
                {config.categories.map((c) => {
                  const total = Math.abs(data?.by_category?.[c.key] || 0);
                  const hasData = total > 0;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setDrill({
                        category: c.key,
                        month: selectedMonth !== "all" ? selectedMonth : null,
                      })}
                      className={`inline-flex items-center justify-center h-8 px-3 rounded-lg border text-xs font-semibold transition min-w-0 ${
                        hasData
                          ? "bg-brand-teal/10 text-brand-teal-dark border-brand-teal/40 hover:bg-brand-teal/20 hover:border-brand-teal"
                          : "bg-white text-brand-navy/50 border-slate-200 hover:border-brand-navy/40 hover:text-brand-navy"
                      }`}
                      data-testid={`ops-dashboard-cat-jump-${c.key}`}
                    >
                      <span className="truncate">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

          {/* Cost centre pill toggle — identical style to the Source
              toggle so the two chained pills read as one control row,
              same visual footprint as Review's Source toggle. */}
          <div className="inline-flex items-center rounded-full bg-white border border-slate-200 p-0.5" data-testid="ops-dashboard-cc-toggle">
            {[
              { key: "", label: "All" },
              ...config.cost_centres.map((c) => ({ key: c.key, label: c.label })),
            ].map(({ key, label }) => {
              const active = (key === "" ? isAll : cc === key);
              return (
                <button
                  key={key || "__all__"}
                  type="button"
                  onClick={() => setCc(key)}
                  className={`h-7 px-3 rounded-full text-xs font-semibold transition ${
                    active
                      ? "bg-brand-navy text-white shadow-sm"
                      : "text-brand-navy/70 hover:text-brand-navy"
                  }`}
                  data-testid={key === "" ? "ops-dashboard-cc-all" : `ops-dashboard-cc-${key}`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Source filter — Statement vs Manual. Identical styling to
              the Review tab's source toggle. */}
          <div className="inline-flex items-center rounded-full bg-white border border-slate-200 p-0.5" data-testid="ops-dashboard-source-toggle">
            {[
              { key: "", label: "All" },
              { key: "import", label: "Statement" },
              { key: "manual", label: "Manual" },
            ].map((opt) => {
              const active = sourceFilter === opt.key;
              return (
                <button
                  key={opt.key || "all"}
                  type="button"
                  onClick={() => setSourceFilter(opt.key)}
                  className={`h-7 px-3 rounded-full text-xs font-semibold transition ${
                    active
                      ? "bg-brand-navy text-white shadow-sm"
                      : "text-brand-navy/70 hover:text-brand-navy"
                  }`}
                  data-testid={`ops-dashboard-source-${opt.key || "all"}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right-side action — matches the "Manual entry" position on
            the Review tab. */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setBudgetsOpen(true)}
            disabled={isAll}
            title={isAll ? "Pick a specific cost centre to edit budgets" : ""}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white border border-brand-teal/50 text-brand-teal hover:bg-brand-teal hover:text-white font-semibold text-xs transition disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="ops-open-budgets-btn"
          >
            <Wallet className="h-3.5 w-3.5" />
            Set budgets
          </button>
        </div>
      </div>

      {/* Loans — cumulative total flagged as is_loan across the whole org.
          Sits above the cost-centre breakdown so it's the first thing the
          operator sees when reconciling reimbursements. */}
      {loans && (loans.count > 0 || loans.total !== 0) && (
        <div
          className="rounded-2xl border border-brand-teal/30 bg-brand-mist/40 p-4 sm:p-5"
          data-testid="ops-dashboard-loans-card"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-brand-teal text-white flex items-center justify-center shrink-0">
              <Landmark className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider font-semibold text-brand-teal-dark">
                Loans · reimbursement tracker
              </p>
              <p className="mt-1 text-2xl font-bold text-brand-navy tabular-nums" data-testid="ops-dashboard-loans-net">
                {loans.total >= 0 ? "+" : "−"}{money(loans.total)}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">
                Net across {loans.count} flagged {loans.count === 1 ? "row" : "rows"} · toggle "Is this a loan?" on any transaction to include it.
              </p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-lg bg-white border border-slate-200 px-3 py-2">
              <p className="uppercase tracking-wider text-slate-500 font-semibold">Received</p>
              <p className="mt-0.5 text-base font-bold text-emerald-700 tabular-nums" data-testid="ops-dashboard-loans-received">
                +{money(loans.received)}
              </p>
              <p className="text-[11px] text-slate-500">Loan disbursements the org owes</p>
            </div>
            <div className="rounded-lg bg-white border border-slate-200 px-3 py-2">
              <p className="uppercase tracking-wider text-slate-500 font-semibold">Paid out</p>
              <p className="mt-0.5 text-base font-bold text-rose-700 tabular-nums" data-testid="ops-dashboard-loans-paid">
                −{money(loans.paid_out)}
              </p>
              <p className="text-[11px] text-slate-500">Repayments already made</p>
            </div>
          </div>
        </div>
      )}

      {data && (
        <>
          {/* Income / Expense / Net */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SummaryCard
              label="Income"
              value={money(data.income)}
              sub={`Money in`}
              icon={TrendingUp}
              tone="emerald"
              testid="ops-summary-income"
            />
            <SummaryCard
              label="Expenses"
              value={money(data.expense)}
              sub={`Money out`}
              icon={TrendingDown}
              tone="rose"
              testid="ops-summary-expense"
            />
            <SummaryCard
              label="Net"
              value={`${data.total >= 0 ? "+" : "−"}${money(data.total)}`}
              sub={`${data.count} transaction${data.count === 1 ? "" : "s"}`}
              icon={data.total >= 0 ? TrendingUp : TrendingDown}
              tone={data.total >= 0 ? "emerald" : "rose"}
              testid="ops-summary-total"
            />
          </div>

          {data.review_pending > 0 && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-center gap-2 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>
                <strong>{data.review_pending}</strong> transaction{data.review_pending === 1 ? "" : "s"} still pending review — totals will move as you sign them off.
              </span>
            </div>
          )}

          <CategoryBreakdown
            byCategory={data.by_category}
            byCategoryMonthly={data.by_category_monthly}
            months={data.months}
            budgets={data.budgets}
            config={config}
            costCentre={cc}
            selectedMonth={selectedMonth}
            onSelectMonth={setSelectedMonth}
            onBudgetSaved={reload}
            onDrill={(payload) => setDrill(payload)}
          />
        </>
      )}

      <BudgetsDialog
        open={budgetsOpen}
        onOpenChange={setBudgetsOpen}
        costCentre={cc}
        costCentreLabel={ccLabel}
        months={data?.months || []}
        categories={config.categories}
        budgets={data?.budgets || {}}
        spendByCategoryMonthly={data?.by_category_monthly || {}}
        defaultMonth={selectedMonth}
        onSaved={reload}
      />

      <TransactionsDrillDialog
        open={!!drill}
        onOpenChange={(o) => { if (!o) setDrill(null); }}
        costCentre={cc || undefined}
        category={drill?.category || ""}
        month={drill?.month || null}
        config={config}
        onChanged={reload}
      />
    </div>
  );
}

function SummaryCard({ label, value, sub, icon: Icon, tone, testid }) {
  const toneClass = tone === "emerald"
    ? "text-emerald-600"
    : tone === "rose"
    ? "text-rose-600"
    : "text-brand-navy";
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
        <Icon className={`h-4 w-4 ${toneClass}`} />
      </div>
      <p className={`mt-2 text-3xl font-bold font-heading tabular-nums ${toneClass}`}>{value}</p>
      <p className="mt-1 text-xs font-medium text-slate-500">{sub}</p>
    </div>
  );
}

function CategoryBreakdown({
  byCategory, config, byCategoryMonthly, months, budgets,
  costCentre, selectedMonth, onSelectMonth, onBudgetSaved, onDrill,
}) {
  const activeCats = config.categories
    .filter((c) => Math.abs(byCategory[c.key] || 0) > 0);

  if (activeCats.length === 0) {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center text-slate-500 text-sm">
        No transactions in this cost centre yet.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ops-dashboard-breakdown">
      <CategoryMonthlyChart
        months={months || []}
        byCategoryMonthly={byCategoryMonthly || {}}
        categories={activeCats}
        selectedMonth={selectedMonth}
        onSelectMonth={onSelectMonth}
        onBarClick={(catKey) => onDrill?.({
          category: catKey,
          month: selectedMonth !== "all" ? selectedMonth : null,
        })}
      />

      <div>
        <h3 className="text-sm font-bold text-brand-navy tracking-tight mb-3 mt-2 uppercase">
          Category detail · budget vs spend
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {activeCats.map((c) => {
            const Icon = CAT_ICON[c.key] || DollarSign;
            return (
              <CategoryDetailCard
                key={c.key}
                category={c}
                Icon={Icon}
                months={months || []}
                spendByMonth={(byCategoryMonthly || {})[c.key] || {}}
                budgetByMonth={(budgets || {})[c.key] || {}}
                selectedMonth={selectedMonth}
                costCentre={costCentre}
                onBudgetSaved={onBudgetSaved}
                onBarClick={(monthKey) => onDrill?.({ category: c.key, month: monthKey })}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
